import { useMemo, useState } from 'react';
import { MapContainer, TileLayer, Polygon, CircleMarker, Tooltip, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { useEffect } from 'react';
import { Download } from 'lucide-react';
import { PRESITE_STATUSES, SURVEY_STATUSES, formatDay, todayUk } from '@nu/shared';
import { STATUS_CLASS } from './store.js';
import MapFullscreen from '../../components/MapFullscreen.jsx';
import { api } from '../../api/client.js';

// The office board's top half: a map of the areas (each its own colour,
// shaded around its jobs, every job a dot) and, beside it, the figures for
// the area under the pointer (or the one clicked; all areas otherwise).
const COLOURS = ['#1f77b4', '#d95f02', '#1b9e77', '#7570b3', '#e7298a', '#66a61e', '#a6761d'];

// The outline around a set of points (convex hull), [[lat, lon]...].
function hull(points) {
  const p = [...points].sort((a, b) => a[1] - b[1] || a[0] - b[0]);
  if (p.length < 3) return p;
  const cross = (o, a, b) => (a[1] - o[1]) * (b[0] - o[0]) - (a[0] - o[0]) * (b[1] - o[1]);
  const lower = [];
  for (const q of p) {
    while (lower.length >= 2 && cross(lower[lower.length - 2], lower[lower.length - 1], q) <= 0) lower.pop();
    lower.push(q);
  }
  const upper = [];
  for (const q of [...p].reverse()) {
    while (upper.length >= 2 && cross(upper[upper.length - 2], upper[upper.length - 1], q) <= 0) upper.pop();
    upper.push(q);
  }
  return [...lower.slice(0, -1), ...upper.slice(0, -1)];
}

function Fit({ points }) {
  const map = useMap();
  const key = points.length;
  useEffect(() => {
    if (points.length) map.fitBounds(points, { padding: [24, 24], maxZoom: 12 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, map]);
  return null;
}

export default function AreaOverview({ areas, jobs, onExport, onPick }) {
  const [hover, setHover] = useState(null);
  const [pinned, setPinned] = useState(null);
  const colour = useMemo(() => Object.fromEntries(areas.map((a, i) => [a.code, COLOURS[i % COLOURS.length]])), [areas]);
  const located = jobs.filter((j) => j.lat != null && j.lon != null);
  const shapes = useMemo(() => areas.map((a) => ({
    code: a.code,
    ring: hull(located.filter((j) => j.areaCode === a.code).map((j) => [j.lat, j.lon])),
  })), [areas, located]);
  // The real postcode districts (LE13, CH1...), outlined on the server; the
  // shape round the jobs is only shown until they arrive.
  const [districts, setDistricts] = useState(null);
  useEffect(() => { api('/surveys/districts').then((d) => setDistricts(d.districts), () => setDistricts([])); }, [jobs.length]);
  const current = hover || pinned;
  const area = areas.find((a) => a.code === current) || null;
  const mine = area ? jobs.filter((j) => j.areaCode === area.code) : jobs;

  return (
    <section className="card area-overview">
      <div className="area-map">
        <div className="area-legend">
          {areas.map((a) => (
            <button key={a.code} type="button" className={`area-key${current === a.code ? ' on' : ''}`}
              onMouseEnter={() => setHover(a.code)} onMouseLeave={() => setHover(null)}
              onClick={() => setPinned(pinned === a.code ? null : a.code)}>
              <span style={{ background: colour[a.code] }} aria-hidden="true" /> {a.name}
            </button>
          ))}
        </div>
        <div className="area-map-box">
          <MapContainer center={[53, -1.8]} zoom={7} scrollWheelZoom style={{ height: '100%', width: '100%' }}>
            <TileLayer url="https://tile.openstreetmap.org/{z}/{x}/{y}.png" attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>' maxZoom={19} />
            <Fit points={located.map((j) => [j.lat, j.lon])} />
            <MapFullscreen />
            {districts?.length > 0 && districts.map((d) => (
              <Polygon key={d.outcode} positions={d.rings}
                pathOptions={{ color: colour[d.areaCode] || '#555', weight: current === d.areaCode ? 2.5 : 1.5, fillOpacity: current === d.areaCode ? 0.32 : 0.16 }}
                eventHandlers={{ mouseover: () => setHover(d.areaCode), mouseout: () => setHover(null), click: () => setPinned(pinned === d.areaCode ? null : d.areaCode) }}>
                <Tooltip sticky>{d.outcode} · {d.jobs} job{d.jobs === 1 ? '' : 's'}</Tooltip>
              </Polygon>
            ))}
            {!districts?.length && shapes.filter((s) => s.ring.length >= 3).map((s) => (
              <Polygon key={s.code} positions={s.ring}
                pathOptions={{ color: colour[s.code], weight: current === s.code ? 3 : 2, fillOpacity: current === s.code ? 0.28 : 0.14 }}
                eventHandlers={{ mouseover: () => setHover(s.code), mouseout: () => setHover(null), click: () => setPinned(pinned === s.code ? null : s.code) }} />
            ))}
            {located.map((j) => (
              <CircleMarker key={j.id} center={[j.lat, j.lon]} radius={4}
                pathOptions={{ color: '#fff', weight: 1, fillColor: colour[j.areaCode] || '#555', fillOpacity: 0.95 }}
                eventHandlers={{ mouseover: () => setHover(j.areaCode), mouseout: () => setHover(null), click: () => onPick(j) }}>
                <Tooltip>{j.address}, {j.postcode} · {j.jobStatus}</Tooltip>
              </CircleMarker>
            ))}
          </MapContainer>
        </div>
      </div>
      <div className="area-metrics">
        <Metrics area={area} jobs={mine} colour={area ? colour[area.code] : null} onExport={onExport} />
      </div>
    </section>
  );
}

function Metrics({ area, jobs, colour, onExport }) {
  const today = todayUk();
  const count = (fn) => jobs.filter(fn).length;
  const total = jobs.length;
  const surveyed = count((j) => j.jobStatus !== 'To Presite');
  const outcomes = PRESITE_STATUSES.map((s) => [s, count((j) => j.presiteStatus === s)]).filter(([, n]) => n);
  const upcoming = jobs.filter((j) => j.plannedDate && j.plannedDate >= today).sort((a, b) => a.plannedDate.localeCompare(b.plannedDate));
  const pct = (n) => (total ? Math.round((n / total) * 100) : 0);

  return (
    <div className="stack metrics">
      <div className="survey-area-head">
        <h2>{area ? <><span className="area-dot" style={{ background: colour }} aria-hidden="true" />{area.name}</> : 'All areas'}</h2>
        {area && (
          <span className="metric-exports">
            <button type="button" className="btn with-icon" onClick={() => onExport(area.code, 'xlsx')}><Download size={16} aria-hidden="true" /> Excel</button>
            <button type="button" className="btn with-icon" onClick={() => onExport(area.code, 'pdf')}><Download size={16} aria-hidden="true" /> PDF</button>
          </span>
        )}
      </div>
      {!area && <p className="small muted">Hover an area on the map (or click it to keep it) to see its figures.</p>}
      <div className="metric-tiles">
        <div><span className="small muted">Jobs</span><strong>{total}</strong></div>
        <div><span className="small muted">Surveyed</span><strong>{surveyed}</strong><span className="small">{pct(surveyed)}%</span></div>
        <div><span className="small muted">Unassigned</span><strong>{count((j) => !j.surveyorId && j.jobStatus === 'To Presite')}</strong></div>
        <div><span className="small muted">No gang</span><strong>{count((j) => !j.teamId)}</strong></div>
      </div>
      <div>
        <h3 className="metric-h">Job Status</h3>
        <div className="metric-bar" aria-hidden="true">
          {SURVEY_STATUSES.map((s) => { const n = count((j) => j.jobStatus === s); return n ? <span key={s} className={STATUS_CLASS[s]} style={{ flexGrow: n }} /> : null; })}
        </div>
        <ul className="metric-list">
          {SURVEY_STATUSES.map((s) => <li key={s}><span className={`status ${STATUS_CLASS[s]}`}>{s}</span><strong>{count((j) => j.jobStatus === s)}</strong></li>)}
        </ul>
      </div>
      {outcomes.length > 0 && (
        <div>
          <h3 className="metric-h">Presite outcomes</h3>
          <ul className="metric-list">{outcomes.map(([s, n]) => <li key={s}><span>{s}</span><strong>{n}</strong></li>)}</ul>
        </div>
      )}
      {upcoming.length > 0 && (
        <div>
          <h3 className="metric-h">Planned next</h3>
          <ul className="metric-list">{upcoming.slice(0, 4).map((j) => <li key={j.id}><span>{j.address}</span><strong>{formatDay(j.plannedDate)}</strong></li>)}</ul>
        </div>
      )}
    </div>
  );
}
